/**
 * LiveRunTrace — the watchable, controllable agent-run view
 * (docs/agents/RUN-TRACE-PRD.md). Server-renders the run header + any events
 * already recorded, then (for a running run) opens the trace stream and appends
 * events as the worker subprocess emits them. A control bar pauses / resumes /
 * aborts the live run. When the run is terminal it shows the outcome and the
 * aggregate per-step RunTrace waterfall.
 *
 * The streaming + control transport goes through the sanctioned `useStream` /
 * `apiCall` seams against the `runTrace` contract — no ad-hoc `fetch`.
 */

import * as React from "react";
import { Alert, AlertDescription, AlertTitle } from "../../alert";
import { Button } from "../../button";
import { Card } from "../../card";
import { TriangleAlert } from "../../icons";
import { classifyRunExit } from "../../tokens/run-exit";
import { SectionTitle } from "../section-title";
import { IntentBadge } from "../status-badge";
import { formatRelativeTime, formatUsd, roleLabel, agentDisplayName } from "../../format";
import { apiCall } from "../../api/client";
import { useStream } from "../../api/client-stream";
import {
  runTrace,
  type RunEventWire,
  type RunTraceFrame,
} from "../../api/contracts/run-trace";
import { RunTrace, type RunTraceStep } from "../optimization/run-trace";

export interface LiveRunHeader {
  id: number;
  role: string;
  agent_id: string;
  model: string | null;
  status: string;
  control_state: string | null;
  started_at: string;
  finished_at: string | null;
  exit_reason: string | null;
}

/**
 * Modern `claude --output-format stream-json` emits many `system` subtypes per
 * run; only the once-per-run `init` marker is worth showing as "session
 * started". Runs recorded before the translator fix persisted a `system` row
 * for EVERY subtype (hook_started, task_notification, thinking_tokens,
 * api_retry, …) — hundreds of identical rows that flood the trace. Drop that
 * noise from the displayed timeline (legacy rows used a generic "system" body;
 * treat that, and a missing body, as the start marker).
 */
function isTraceNoise(e: RunEventWire): boolean {
  return e.kind === "system" && !!e.body && e.body !== "init" && e.body !== "system";
}

function parseMeta(meta: string | null): Record<string, unknown> {
  if (!meta) return {};
  try {
    const v = JSON.parse(meta) as unknown;
    return v && typeof v === "object" ? (v as Record<string, unknown>) : {};
  } catch {
    return {};
  }
}

export function LiveRunTrace({
  run,
  initialEvents,
  steps = [],
  embedded = false,
}: {
  run: LiveRunHeader;
  initialEvents: RunEventWire[];
  steps?: RunTraceStep[];
  /**
   * Render inside a host that already shows the run's identity (title, state
   * badge, key-facts band) — i.e. the ObjectPage-grammar run page. Drops the
   * redundant status/meta bar for a terminal run and the duplicated
   * role·agent·model line, keeping only the live control bar while running.
   */
  embedded?: boolean;
}) {
  const stream = useStream(runTrace.stream);
  const started = React.useRef(false);
  const [pending, setPending] = React.useState<string | null>(null);

  // Open the stream once, only for a run that was live at render time. A run
  // that finishes mid-stream is closed by the server's terminal `done` frame.
  React.useEffect(() => {
    if (run.status === "running" && !started.current) {
      started.current = true;
      const since = initialEvents.length
        ? initialEvents[initialEvents.length - 1]!.seq
        : 0;
      void stream.send({ runId: run.id, since });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const frames = stream.frames as RunTraceFrame[];

  // Merge server-rendered events with streamed ones, deduped + ordered by seq.
  const events = React.useMemo(() => {
    const map = new Map<number, RunEventWire>();
    for (const e of initialEvents) map.set(e.seq, e);
    for (const f of frames) if (f.type === "event") map.set(f.event.seq, f.event);
    return [...map.values()]
      .filter((e) => !isTraceNoise(e))
      .sort((a, b) => a.seq - b.seq);
  }, [initialEvents, frames]);

  // Latest status/control comes from the most recent status frame, else the
  // server-rendered values.
  const lastStatus = React.useMemo(() => {
    for (let i = frames.length - 1; i >= 0; i--) {
      const f = frames[i]!;
      if (f.type === "status") return f;
    }
    return null;
  }, [frames]);
  const status = lastStatus?.status ?? run.status;
  const controlState = lastStatus?.control_state ?? run.control_state;
  const isRunning = status === "running";
  const isPaused = controlState === "paused";
  const isAborting = controlState === "aborting";

  async function control(action: "pause" | "resume" | "abort") {
    setPending(action);
    try {
      await apiCall(runTrace.control, { body: { runId: run.id, action } });
    } finally {
      setPending(null);
    }
  }

  // When embedded, the host page's header + key-facts band already show the
  // run's outcome + identity, so the status bar is only worth rendering while the
  // run is live (it carries the pause/resume/abort controls).
  const showControlBar = !embedded || isRunning;

  // WHY it ended. `exit_reason` has always been passed into this component and
  // never rendered, so the run page could tell you a run was "Budget exceeded"
  // and never that the watchdog stopped it at its wall-clock ceiling. It is the
  // first thing you want on a run you opened BECAUSE it failed, so it sits
  // above the trace rather than under it.
  //
  // Only for a run that ended badly: a clean run's reason is "ok", and an
  // alert saying so is noise on every successful run in the fleet.
  const exitBucket = classifyRunExit(run.exit_reason);
  const endedBadly = !isRunning && status !== "completed" && run.exit_reason != null;

  return (
    <div className="flex flex-col gap-4">
      {endedBadly ? (
        <Alert variant={exitBucket.key === "error-exit" ? "destructive" : "default"}>
          <TriangleAlert />
          <AlertTitle>{exitBucket.label}</AlertTitle>
          <AlertDescription className="flex flex-col gap-1">
            <span>{exitBucket.hint}</span>
            {/* The raw marker, verbatim, underneath the sentence — the page
                stays readable without ever becoming un-checkable against the
                ledger it is reporting. */}
            {/* `break-words`, not `break-all`: the marker is wide enough here to
                wrap, and break-all split it mid-token ("(cli=cla / ude)"). */}
            <code className="font-mono type-subtext break-words opacity-70">{run.exit_reason}</code>
          </AlertDescription>
        </Alert>
      ) : null}

      {/* live + control bar */}
      {showControlBar ? (
      <Card className="flex flex-wrap items-center justify-between gap-3 p-3">
        <div className="flex items-center gap-2">
          {isRunning ? (
            <span className="flex items-center gap-2">
              <span
                className={`inline-block size-2 rounded-full ${
                  isPaused ? "bg-warning" : "bg-success animate-pulse"
                }`}
                aria-hidden
              />
              <span className="type-body text-foreground">
                {isAborting
                  ? "Aborting…"
                  : isPaused
                    ? "Paused"
                    : "Watching live"}
              </span>
            </span>
          ) : (
            <RunOutcomeBadge status={status} />
          )}
          {embedded ? null : (
            <span className="type-subtext text-muted-foreground">
              {roleLabel(run.role)} · {agentDisplayName(run.agent_id)} · {run.model ?? "model n/a"}
            </span>
          )}
        </div>
        {isRunning ? (
          <div className="flex items-center gap-2">
            {isPaused ? (
              <Button
                variant="outline"
                onClick={() => control("resume")}
                disabled={pending !== null || isAborting}
              >
                Resume
              </Button>
            ) : (
              <Button
                variant="outline"
                onClick={() => control("pause")}
                disabled={pending !== null || isAborting}
              >
                Pause
              </Button>
            )}
            <Button
              variant="destructive"
              onClick={() => control("abort")}
              disabled={pending !== null || isAborting}
            >
              Abort
            </Button>
          </div>
        ) : null}
      </Card>
      ) : null}

      {/* the live event timeline */}
      <Card className="flex flex-col gap-3 p-4">
        <SectionTitle
          variant="plain"
          title="Trace"
          action={
            isRunning && stream.status === "streaming" ? (
              <span className="type-subtext text-muted-foreground">
                {events.length} events
              </span>
            ) : undefined
          }
        />
        {events.length === 0 ? (
          <p className="type-body text-muted-foreground">
            {isRunning
              ? "Waiting for the agent to start…"
              : "No live trace recorded for this run (it predates live tracing, or ran on a CLI without streaming)."}
          </p>
        ) : (
          <ol className="flex flex-col gap-2">
            {events.map((e) => (
              <EventRow key={e.seq} event={e} />
            ))}
          </ol>
        )}
        {stream.error ? (
          <p className="type-subtext text-destructive">stream: {stream.error}</p>
        ) : null}
      </Card>

      {/* aggregate per-step waterfall (the optimizer's view), when present */}
      {steps.length > 0 ? (
        <div className="flex flex-col gap-2">
          <SectionTitle variant="plain" title="Steps" />
          <RunTrace steps={steps} />
        </div>
      ) : null}
    </div>
  );
}

function RunOutcomeBadge({ status }: { status: string }) {
  const intent: "success" | "danger" | "warning" | "neutral" =
    status === "completed"
      ? "success"
      : status === "failed"
        ? "danger"
        : status === "aborted" || status === "budget_exceeded"
          ? "warning"
          : "neutral";
  return <IntentBadge intent={intent}>{status}</IntentBadge>;
}

function EventRow({ event }: { event: RunEventWire }) {
  const meta = parseMeta(event.meta);
  const when = (
    <span className="shrink-0 type-subtext tabular-nums text-muted-foreground">
      {formatRelativeTime(event.created_at)}
    </span>
  );

  switch (event.kind) {
    case "system":
      return (
        <li className="flex items-center justify-between gap-2 border-b border-border/40 pb-2 last:border-b-0">
          <span className="type-subtext text-muted-foreground">
            session started{meta.model ? ` · ${String(meta.model)}` : ""}
          </span>
          {when}
        </li>
      );
    case "text":
      return (
        <li className="flex flex-col gap-1 border-b border-border/40 pb-2 last:border-b-0">
          <div className="flex items-center justify-between gap-2">
            <span className="type-subtext text-muted-foreground">assistant</span>
            {when}
          </div>
          <p className="whitespace-pre-wrap break-words type-body text-foreground">
            {event.body}
          </p>
        </li>
      );
    case "thinking":
      return (
        <li className="flex flex-col gap-1 border-b border-border/40 pb-2 last:border-b-0">
          <span className="type-subtext text-muted-foreground">thinking</span>
          <p className="whitespace-pre-wrap break-words type-body italic text-muted-foreground">
            {event.body}
          </p>
        </li>
      );
    case "tool_use":
      return (
        <li className="flex flex-col gap-1 border-b border-border/40 pb-2 last:border-b-0">
          <div className="flex items-center justify-between gap-2">
            <span className="flex items-center gap-2 type-body text-foreground">
              <IntentBadge intent="info">tool</IntentBadge>
              <span className="font-mono type-subtext">{event.tool_name}</span>
            </span>
            {when}
          </div>
          {event.body ? (
            <pre className="max-h-32 overflow-y-auto whitespace-pre-wrap break-words rounded bg-selected px-2 py-1 type-subtext text-muted-foreground">
              {event.body}
            </pre>
          ) : null}
        </li>
      );
    case "tool_result": {
      const ok = meta.ok !== false;
      return (
        <li className="flex flex-col gap-1 border-b border-border/40 pb-2 pl-3 last:border-b-0">
          <div className="flex items-center justify-between gap-2">
            <span className="flex items-center gap-2 type-subtext">
              <IntentBadge intent={ok ? "success" : "danger"}>
                {ok ? "result" : "error"}
              </IntentBadge>
            </span>
            {when}
          </div>
          {event.body ? (
            <p className="whitespace-pre-wrap break-words type-subtext text-muted-foreground">
              {event.body}
            </p>
          ) : null}
        </li>
      );
    }
    case "control":
      return (
        <li className="flex items-center justify-between gap-2 border-b border-border/40 pb-2 last:border-b-0">
          <span className="flex items-center gap-2 type-subtext">
            <IntentBadge intent="warning">{event.body}</IntentBadge>
            {meta.state ? (
              <span className="text-muted-foreground">→ {String(meta.state)}</span>
            ) : null}
          </span>
          {when}
        </li>
      );
    case "result": {
      const cost = typeof meta.cost_usd === "number" ? meta.cost_usd : null;
      const inTok = typeof meta.input_tokens === "number" ? meta.input_tokens : null;
      const outTok = typeof meta.output_tokens === "number" ? meta.output_tokens : null;
      return (
        <li className="flex flex-col gap-1 border-b border-border/40 pb-2 last:border-b-0">
          <div className="flex items-center justify-between gap-2">
            <span className="flex items-center gap-2 type-body text-foreground">
              <IntentBadge intent={meta.is_error ? "danger" : "success"}>
                result
              </IntentBadge>
              <span className="type-subtext text-muted-foreground tabular-nums">
                {cost !== null ? formatUsd(cost) : ""}
                {inTok !== null || outTok !== null
                  ? ` · ${inTok ?? "?"}/${outTok ?? "?"} tok`
                  : ""}
              </span>
            </span>
            {when}
          </div>
          {event.body ? (
            <p className="whitespace-pre-wrap break-words type-body text-foreground">
              {event.body}
            </p>
          ) : null}
        </li>
      );
    }
    case "error": {
      // A provider/transport failure (#44955) carries structured meta from the
      // harness error channel (#44930): subtype/status/provider/model. Badge it
      // so the operator sees the CAUSE (401 · deepseek), not just red text; a
      // plain error row (no provider meta) renders as before.
      const provider = typeof meta.provider === "string" ? meta.provider : null;
      const status = typeof meta.status === "number" ? meta.status : null;
      const isProviderError = meta.subtype === "provider_error";
      if (!isProviderError && !provider && status === null) {
        return (
          <li className="flex items-center justify-between gap-2 border-b border-border/40 pb-2 last:border-b-0">
            <span className="type-subtext text-destructive">{event.body}</span>
            {when}
          </li>
        );
      }
      return (
        <li className="flex flex-col gap-1 border-b border-border/40 pb-2 last:border-b-0">
          <div className="flex items-center justify-between gap-2">
            <span className="flex items-center gap-2 type-subtext">
              <IntentBadge intent="danger">
                {isProviderError ? "provider error" : "error"}
              </IntentBadge>
              {provider || status !== null ? (
                <span className="text-muted-foreground">
                  {[provider, status !== null ? `HTTP ${status}` : null]
                    .filter(Boolean)
                    .join(" · ")}
                </span>
              ) : null}
            </span>
            {when}
          </div>
          <span className="whitespace-pre-wrap break-words type-subtext text-destructive">
            {event.body}
          </span>
        </li>
      );
    }
    default:
      return null;
  }
}
